import { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Alert,
  Chip,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { chipSx } from "@shared/utils/theme";
import { submissionLateness } from "../utils/lateness";
import { useNotification } from "@shared/contexts/NotificationContext";
import {
  getResourceGrades,
  setStudentGrade,
  updateReadingStatus,
  updateCanvasStatus,
  updatePaperStatus,
} from "@services/api";
import type { ResourceGradeRow } from "@/types/models";
import type { WorkspaceResourceView } from "@/types/workspace";

interface Props {
  open: boolean;
  resource: WorkspaceResourceView | null; // reading | canvas (gradable)
  workspaceId: string;
  onClose: () => void;
  onSaved: () => void;
}

// Local editable mirror of a student's grade row.
interface RowState {
  userId: string;
  username: string;
  status: string | null;
  pointsInput: string; // text field value ("" = ungraded)
  released: boolean;
  saving: boolean;
  submittedAt: string | null; // when they turned it in (for late/on-time)
}

const statusLabel = (s: string | null): string => {
  switch (s) {
    case "submitted":
    case "completed":
      return "Submitted";
    case "returned":
      return "Returned";
    case "in_progress":
      return "In progress";
    case "assigned":
      return "Not started";
    default:
      return "—";
  }
};

// Maps a status to a shared chip hue (null → no chip, render a dash).
const statusHue = (s: string | null): string | null => {
  switch (s) {
    case "submitted":
    case "completed":
      return "submitted";
    case "returned":
      return "returned";
    case "in_progress":
      return "in_progress";
    case "assigned":
      return "not_started";
    default:
      return null;
  }
};

export default function GradesModal({
  open,
  resource,
  workspaceId,
  onClose,
  onSaved,
}: Props) {
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(false);
  const [maxPoints, setMaxPoints] = useState<number | null>(null);
  const [rows, setRows] = useState<RowState[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  const type =
    resource?.type === "canvas"
      ? ("canvas" as const)
      : resource?.type === "paper"
        ? ("paper" as const)
        : ("reading" as const);

  useEffect(() => {
    if (!open || !resource) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    getResourceGrades(type, workspaceId, resource.id).then((res) => {
      if (cancelled) return;
      setLoading(false);
      if (!res.success) {
        setLoadError(res.error.message || "Could not load grades.");
        return;
      }
      setMaxPoints(res.data.max_points);
      setRows(
        res.data.students.map((s: ResourceGradeRow) => ({
          userId: s.user_id,
          username: s.username,
          status: s.status,
          pointsInput: s.points != null ? String(s.points) : "",
          released: s.released,
          saving: false,
          submittedAt: s.submitted_at,
        }))
      );
    });
    return () => {
      cancelled = true;
    };
  }, [open, resource, type, workspaceId]);

  const patchRow = (userId: string, patch: Partial<RowState>) =>
    setRows((rs) =>
      rs.map((r) => (r.userId === userId ? { ...r, ...patch } : r))
    );

  const saveRow = async (row: RowState, releasedOverride?: boolean) => {
    if (!resource) return;
    const released = releasedOverride ?? row.released;
    // Blank points = ungraded (null); otherwise the entered grade.
    const trimmed = row.pointsInput.trim();
    const points = trimmed === "" ? null : Number(trimmed);
    if (points != null && Number.isNaN(points)) {
      showError("Points must be a number.");
      return;
    }
    patchRow(row.userId, { saving: true });
    const res = await setStudentGrade(
      type,
      workspaceId,
      resource.id,
      row.userId,
      { points, released }
    );
    patchRow(row.userId, { saving: false, released });
    if (!res.success) {
      showError(res.error.message || "Could not save grade.");
      return;
    }
    showSuccess(`Saved ${row.username}'s grade.`);
    onSaved();
  };

  const releaseAll = async () => {
    for (const row of rows) {
      // Sequential to keep it simple; rosters are small.
      // eslint-disable-next-line no-await-in-loop
      await saveRow(row, true);
    }
  };

  // Instructor "unsubmit" — return a submitted student's work for
  // revision. Sets the 'returned' status (distinct from a never-
  // submitted draft) so the student sees a clear "Returned" signal and
  // can revise and turn it in again. Uses the owner-widened per-user
  // status endpoint for the resource type.
  const unsubmitRow = async (row: RowState) => {
    if (!resource) return;
    if (
      !window.confirm(
        `Return ${row.username}'s submission for revision? They'll be able to edit and turn it in again.`
      )
    )
      return;
    patchRow(row.userId, { saving: true });
    const res =
      type === "reading"
        ? await updateReadingStatus(resource.id, row.userId, "returned")
        : type === "canvas"
          ? await updateCanvasStatus(
              workspaceId,
              resource.id,
              row.userId,
              "returned"
            )
          : await updatePaperStatus(
              workspaceId,
              resource.id,
              row.userId,
              "returned"
            );
    patchRow(row.userId, { saving: false });
    if (!res.success) {
      showError(res.error.message || "Could not return the submission.");
      return;
    }
    patchRow(row.userId, { status: "returned" });
    showSuccess(`Returned ${row.username}'s submission for revision.`);
    onSaved();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      // Grading is a focused task — give it real room (~70% of the
      // viewport) without going full-screen. The student list scrolls
      // inside the fixed-height paper.
      maxWidth={false}
      PaperProps={{
        sx: {
          width: { xs: "95vw", sm: "70vw" },
          maxWidth: 1100,
          height: { xs: "90vh", sm: "75vh" },
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 600, pr: 6 }}>
        Grades — {resource?.name || "Untitled"}
        {maxPoints != null && (
          <Typography variant="body2" color="text.secondary">
            Out of {maxPoints} points
          </Typography>
        )}
      </DialogTitle>
      <IconButton
        aria-label="Close"
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (t) => t.palette.text.secondary,
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent>
        {loading ? (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress />
          </Stack>
        ) : loadError ? (
          <Typography color="error" sx={{ py: 2 }}>
            {loadError}
          </Typography>
        ) : rows.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            No students are assigned to this resource yet.
          </Typography>
        ) : (
          <Table size="small">
            <caption style={{ captionSide: "top", padding: 0 }}>
              <Alert severity="info" sx={{ mb: 1 }}>
                Enter each student's grade and release it when ready. Leave
                points blank to leave a student ungraded. (The rubric-assisted
                grading lives in the annogram grading sidebar.)
              </Alert>
            </caption>
            <TableHead>
              <TableRow>
                <TableCell>Student</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Points</TableCell>
                <TableCell align="center">Released</TableCell>
                <TableCell align="right">Save</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.userId}>
                  <TableCell>{row.username}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {(() => {
                        const hue = statusHue(row.status);
                        return hue ? (
                          <Chip
                            label={statusLabel(row.status)}
                            size="small"
                            variant="outlined"
                            sx={chipSx(hue)}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        );
                      })()}
                      {(row.status === "submitted" ||
                        row.status === "completed") &&
                        (() => {
                          const l = submissionLateness(
                            row.submittedAt,
                            resource?.dueDate ?? null
                          );
                          if (!l) return null;
                          return (
                            <Typography
                              variant="caption"
                              sx={(t) => ({
                                color: l.late
                                  ? t.palette.mode === "dark"
                                    ? "#ff8a80"
                                    : "#b71c1c"
                                  : t.palette.text.secondary,
                              })}
                            >
                              {l.label}
                            </Typography>
                          );
                        })()}
                      {(row.status === "submitted" ||
                        row.status === "completed") && (
                        <Button
                          size="small"
                          variant="text"
                          color="secondary"
                          onClick={() => unsubmitRow(row)}
                          disabled={row.saving}
                          aria-label={`Return ${row.username}'s submission for revision`}
                        >
                          Return
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      value={row.pointsInput}
                      onChange={(e) =>
                        patchRow(row.userId, { pointsInput: e.target.value })
                      }
                      size="small"
                      inputProps={{
                        inputMode: "numeric",
                        style: { width: 56, textAlign: "center" },
                        "aria-label": `points for ${row.username}`,
                      }}
                      placeholder="—"
                      disabled={row.saving}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={row.released}
                      onChange={(e) =>
                        patchRow(row.userId, { released: e.target.checked })
                      }
                      disabled={row.saving}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      aria-label={`Save grade for ${row.username}`}
                      onClick={() => saveRow(row)}
                      disabled={row.saving}
                    >
                      {row.saving ? <CircularProgress size={16} /> : "Save"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {rows.length > 0 && !loading && (
          <Button onClick={releaseAll} color="secondary">
            Release all
          </Button>
        )}
        <Button onClick={onClose} variant="contained">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
